<template>
  <base-modal
    ref="$modal"
    fullscreen-on-mobile
    :loading="isLoading"
    :title="$t('job_info_title')"
    @on-close="deactivateEsc"
  >
    <div ref="$content" class="job-info">
      <template v-if="!isLoading && jobInfo">
      <div class="job-info__header">
        <div class="job-info__header-left">
          <div class="job-info__status-block">
            <div
              class="job-info__status-stripe"
              :style="{
                backgroundColor: getJobStatusBarColor(jobInfo.Job.Status),
              }"
            />
            <div class="job-info__status-content">
              <div class="job-info__field">
                <span class="job-info__field-name">{{ $t('creation_date') }}:&nbsp;</span>
                <span class="job-info__field-value">{{
                  formatDateTime(jobInfo.Job.CreateTime)
                }}</span>
              </div>
              <div class="job-info__field">
                <span class="job-info__field-name">{{ $t('creator') }}:&nbsp;</span>
                <span class="job-info__field-value">{{
                  jobInfo.Job.Creator?.Name
                }}</span>
              </div>
              <div class="job-info__field">
                <span class="job-info__field-name">{{ $t('responsible') }}:&nbsp;</span>
                <span class="job-info__field-value">{{
                  jobInfo.Job.Worker?.Name
                }}</span>
              </div>
              <div v-if="jobInfo.Job.ToDoDate" class="job-info__field">
                <span class="job-info__field-name">{{ $t('job_todo_by') }}:</span>
                <span class="job-info__field-value">{{
                  formatDateTime(jobInfo.Job.ToDoDate)
                }}</span>
              </div>
              <div v-if="jobInfo.Job.CompletionDate" class="job-info__field">
                <span class="job-info__field-name">{{ $t('job_state_completed') }}:&nbsp;</span>
                <span class="job-info__field-value">{{
                  formatDateTime(jobInfo.Job.CompletionDate)
                }}</span>
              </div>
            </div>
          </div>
          <div class="job-info__field job-info__field--col">
            <div class="job-info__field-name">{{ $t('job_content') }}:</div>
            <div class="job-info__field-message" v-html="jobInfo.Job.Message" />
          </div>
          <div v-if="jobInfo.Job.Additions?.length" class="job-info__additions">
            <div
              v-for="(addition, i) in jobInfo.Job.Additions"
              :key="i"
              class="job-info__addition"
            >
              <span class="job-info__addition-date">{{
                formatAdditionDate(addition.Date)
              }}</span>
              [{{ addition.Username }}] {{ addition.Title }}
              {{ addition.Message }}
            </div>
          </div>
          <div
            v-if="jobInfo.Job.Tags?.length"
            class="job-info__field job-info__field--col"
          >
            <div class="job-info__field-name">{{ $t('tags_upcase') }}:</div>
            <div class="job-info__tags">
              <span
                v-for="tag in jobInfo.Job.Tags"
                :key="tag.Id"
                class="job-info__tag"
              >
                #{{ tag.Name?.toLowerCase() }}
              </span>
            </div>
          </div>
          <div
            v-if="jobInfo.Job.Project"
            class="job-info__field job-info__field--col"
          >
            <div class="job-info__field-name">{{ $t('project_label') }}:</div>
            <router-link
              class="job-info__link"
              :to="{
                query: {
                  page: 'project',
                  id: jobInfo.Job.Project.Id,
                },
              }"
              @click="$modal?.onClose()"
            >
              {{ jobInfo.Job.Project.Name }}
            </router-link>
          </div>
          <div
            v-if="jobInfo.Job.Clients?.length"
            class="job-info__field job-info__field--col"
          >
            <div class="job-info__field-name">
              {{ $t(jobInfo.Job.Clients.length === 1 ? 'client_label' : 'clients_label') }}:
            </div>
            <router-link
              v-for="client in jobInfo.Job.Clients"
              :key="client.Id"
              class="job-info__link"
              :to="{
                query: {
                  page: 'client',
                  id: client.Id,
                },
              }"
              @click="$modal?.onClose()"
            >
              {{ client.Name }}
            </router-link>
          </div>
        </div>
        <div class="job-info__header-right">
          <div class="job-info__add-lists">
            <app-resizable-dropdown
              position="left"
              :resizable="false"
              :initial-width="320"
              :initial-height="436"
              :close-by-click-outside="false"
              :content-z-index="10003"
              :mobile-title="$t('choose_lists')"
              ref="$createEditFileModal"
              name="$createFileModal"
            >
              <template #activator>
                <app-tooltip :text="$t('add_to_list')">
                  <template #activator>
                    <app-icon
                      class="job-info__add-lists-btn"
                      name="plus"
                      width="14px"
                      color="#979797"
                      hover-color="#499259"
                      cursor="pointer"
                    />
                  </template>
                </app-tooltip>
              </template>
              <main-info-list-select
                :id="jobInfo?.Job.Id"
                :added-lists="jobInfo?.AnalyticsLists"
                show-close-icon
                :entity-type="2"
                @close="closeAddJobDropdown"
                @add-list="updateAnalyticLists"
              />
            </app-resizable-dropdown>
            <div class="job-info__add-lists-text">{{ $t('file_info_lists') }}:</div>
          </div>
          <div class="job-info__lists">
            <div
              v-for="list in jobInfo?.AnalyticsLists"
              class="job-info__lists-item"
              :key="list.Id"
            >
              <!-- <div class="contact-info__lists-item-dot" /> -->
              <div class="job-info__lists-item-name">
                {{ list.Name }}
              </div>
              <app-icon
                class="job-info__lists-item-delete"
                name="close"
                width="12px"
                color="#D2D2D2"
                hover-color="#499259"
                cursor="pointer"
                @click="onDeleteList(list)"
              />
            </div>
          </div>
        </div>
      </div>
      </template>
    </div>
  </base-modal>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRoute } from "vue-router";
import BaseModal from "@/components/common/modal/BaseModal.vue";
import { JobInfo } from "@/store/jobs";
import { JobsApi } from "@/ api/JobsApi";
import { JobStatus } from "@/models/etc/types";
import moment from "moment/moment";
import { AnalyticsApi } from "@/ api/AnalyticsApi";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import { AnalyticsListItem } from "@/store/clients";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import MainInfoListSelect from "../../listSelect/MainInfoListSelect.vue";
import { useModalEscape } from "@/composables/useModalEscape";

const route = useRoute();

const $modal = ref<typeof BaseModal | null>(null);
const $content = ref<HTMLElement | null>(null);
const isLoading = ref(false);
const jobInfo = ref<JobInfo | null>(null);

const { activate: activateEsc, deactivate: deactivateEsc } = useModalEscape({
  content: $content,
  close: () => $modal.value?.onClose(),
});

const getJobStatusBarColor = (status: JobStatus) => {
  switch (status) {
    case JobStatus.Standard:
      return "#9A9C9E";
    case JobStatus.Working:
      return "#86CED2";
    case JobStatus.WaitingForCheck:
      return "#FFE7AA";
    case JobStatus.Completed:
      return "#6DA279";
    case JobStatus.Lost:
      return "#EE966A";
  }
};

const $createEditFileModal = ref<typeof AppResizableDropdown | null>(null);

const closeAddJobDropdown = () => {
  $createEditFileModal.value?.close();
};

const updateAnalyticLists = async () => {
  if (!jobInfo.value) return;

  jobInfo.value.AnalyticsLists = (
    await AnalyticsApi.getListOfElement(jobInfo.value.Job.Id, 2)
  ).Lists;
};

const onDeleteList = async (list: AnalyticsListItem) => {
  if (!jobInfo.value) return;
  await AnalyticsApi.removeElementIntoList(list.Id, 2, jobInfo?.value?.Job.Id);
  jobInfo.value.AnalyticsLists.splice(
    jobInfo.value?.AnalyticsLists.findIndex(
      (listFile) => listFile.Id === list.Id,
    ),
    1,
  );
};

const formatAdditionDate = (date?: string | Date) => {
  if (!date) return "";
  return moment(date).format("HH:mm DD.MM.YY");
};

const formatDateTime = (date?: string | Date) => {
  if (!date) return "";
  return moment(date).format("HH:mm DD.MM.YYYY");
};

const onOpen = async (jobId: number) => {
  try {
    isLoading.value = true;
    $modal.value?.onOpen();
    activateEsc();
    jobInfo.value = await JobsApi.getJobInfo(jobId);
  } catch (e) {
    console.error(e);
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ onOpen });
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.job-info {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 12px;

  &__header {
    display: flex;
    width: 100%;
    gap: 8px;

    // &-left {
    //   display: flex;
    //   flex-direction: column;
    //   width: 100%;
    //   flex: 1;
    //   gap: 4px;
    // }

    &-left,
    &-right {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    &-left {
      flex: 2;
    }

    &-right {
      flex: 1;
    }
  }

  &__status-block {
    position: relative;
    padding: 0 0 0 16px;
    margin-bottom: 8px;
    display: inline-block;
  }

  &__status-stripe {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 8px;
    border-top-left-radius: 8px 12px;
    border-bottom-left-radius: 8px 12px;
  }

  &__status-content {
    display: flex;
    flex-direction: column;

    .job-info__field:last-child {
      margin-bottom: 0;
    }
  }

  &__field {
    margin-bottom: 4px;
    font-size: 14px;
    line-height: 18px;
    color: #4d4d4d;

    &--col {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    &-name {
      font-weight: 500;
    }

    &-message {
      font-size: 14px;
      line-height: 18px;
      color: #4d4d4d;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }

  &__link {
    color: #499259;
    text-decoration: none;
    cursor: pointer;
    width: fit-content;

    &:hover {
      text-decoration: underline;
    }
  }

  &__additions {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-bottom: 4px;
  }

  &__addition {
    font-size: 14px;
    line-height: 18px;
    color: #4d4d4d;

    &-date {
      color: #979797;
    }
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__tag {
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    background-color: #f2f2f2;
    border-radius: 8px;
    font-size: 12px;
    line-height: 16px;
    color: #499259;
  }

  &__add-lists {
    display: flex;
    align-items: center;
    gap: 6px;

    &-text {
      color: $color-text_main;
      font-size: 14px;
      line-height: 18px;
      font-weight: 500;
    }

    &-btn {
      margin-top: -2px;
    }
  }

  &__lists {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 2px;
    max-width: 290px;

    &-item {
      display: inline-flex;
      align-items: center;
      font-size: 14px;
      line-height: 18px;
      gap: 2px;
      color: $color-text_main;

      &-dot {
        height: 1px;
        width: 1px;
        border-radius: 100%;
      }
    }
  }
}
</style>
